import React from "react";
import { ArrowDown, ArrowUp, Plus, Trash2 } from "lucide-react";
import { AppButton, FormField, IconButton, Input, Textarea } from "../../ui";

/**
 * ListEditor — the repeatable rows on the event-page form: highlights, agenda,
 * speakers, FAQs.
 *
 * Two shapes, one component:
 *   - a list of strings (`fields` omitted): one input per row, and Enter in the
 *     last row starts the next — the fast way to type a bulleted list;
 *   - a list of objects: `fields` describes the inputs of one row.
 *
 * Rows reorder with the arrows because order is meaning here: it is the order
 * the website shows them in.
 *
 * Props:
 *   items      — the array being edited
 *   onChange   — called with the next array
 *   fields     — [{ key, label, placeholder, multiline, full, maxLength }] for object rows
 *   itemLabel  — "Highlight", "Speaker"… for the row's accessible names
 *   addLabel   — the add button's text
 *   max        — the most rows allowed
 *   blank      — what a new object row starts as
 *   renderRowExtra — (item, index, update) => node, rendered beside an object row's fields
 */
const ListEditor = ({
  items = [],
  onChange,
  fields,
  itemLabel = "Item",
  addLabel = "Add",
  placeholder = "",
  max = 12,
  blank = {},
  maxLength = 200,
  renderRowExtra,
}) => {
  const rows = Array.isArray(items) ? items : [];
  const isStrings = !fields;
  const inputRefs = React.useRef([]);

  const update = (index, next) => onChange(rows.map((row, i) => (i === index ? next : row)));
  const remove = (index) => onChange(rows.filter((_, i) => i !== index));
  const move = (index, delta) => {
    const target = index + delta;
    if (target < 0 || target >= rows.length) return;
    const next = [...rows];
    [next[index], next[target]] = [next[target], next[index]];
    onChange(next);
  };
  const add = () => {
    if (rows.length >= max) return;
    onChange([...rows, isStrings ? "" : { ...blank }]);
    // Focus the new row once it exists.
    requestAnimationFrame(() => inputRefs.current[rows.length]?.focus());
  };

  const rowActions = (index) => (
    <div className="tw-flex tw-shrink-0 tw-items-center tw-gap-0.5">
      <IconButton type="button" label={`Move ${itemLabel.toLowerCase()} up`} icon={ArrowUp} size="xs" disabled={index === 0} onClick={() => move(index, -1)} />
      <IconButton type="button" label={`Move ${itemLabel.toLowerCase()} down`} icon={ArrowDown} size="xs" disabled={index === rows.length - 1} onClick={() => move(index, 1)} />
      <IconButton type="button" label={`Remove ${itemLabel.toLowerCase()}`} icon={Trash2} size="xs" variant="ghost-destructive" onClick={() => remove(index)} />
    </div>
  );

  return (
    <div className="tw-flex tw-flex-col tw-gap-2">
      {rows.map((row, index) => (
        isStrings ? (
          // Index keys: a row is its position, and the value is being typed.
          // eslint-disable-next-line react/no-array-index-key
          <div key={index} className="tw-flex tw-items-center tw-gap-2">
            <Input
              ref={(el) => { inputRefs.current[index] = el; }}
              value={row}
              maxLength={maxLength}
              placeholder={placeholder}
              aria-label={`${itemLabel} ${index + 1}`}
              onChange={(e) => update(index, e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  if (index === rows.length - 1 && row.trim()) add();
                }
              }}
            />
            {rowActions(index)}
          </div>
        ) : (
          // eslint-disable-next-line react/no-array-index-key
          <div key={index} className="tw-rounded-md tw-border tw-border-line tw-bg-surface-alt tw-p-3">
            <div className="tw-mb-2 tw-flex tw-items-center tw-justify-between tw-gap-2">
              <span className="tw-text-xs tw-font-semibold tw-text-content-subtle">
                {itemLabel} {index + 1}
              </span>
              {rowActions(index)}
            </div>
            <div className="tw-flex tw-flex-col tw-gap-3 sm:tw-flex-row">
              {renderRowExtra && <div className="tw-shrink-0">{renderRowExtra(row, index, (next) => update(index, next))}</div>}
              <div className="tw-grid tw-min-w-0 tw-flex-1 tw-grid-cols-1 tw-gap-3 md:tw-grid-cols-2">
                {fields.map((f) => (
                  <div key={f.key} className={f.full ? "md:tw-col-span-2" : undefined}>
                    <FormField label={f.label}>
                      {f.multiline ? (
                        <Textarea
                          ref={f === fields[0] ? (el) => { inputRefs.current[index] = el; } : undefined}
                          rows={f.rows || 2}
                          value={row?.[f.key] || ""}
                          maxLength={f.maxLength || 1500}
                          placeholder={f.placeholder}
                          onChange={(e) => update(index, { ...row, [f.key]: e.target.value })}
                        />
                      ) : (
                        <Input
                          ref={f === fields[0] ? (el) => { inputRefs.current[index] = el; } : undefined}
                          value={row?.[f.key] || ""}
                          maxLength={f.maxLength || 200}
                          placeholder={f.placeholder}
                          onChange={(e) => update(index, { ...row, [f.key]: e.target.value })}
                        />
                      )}
                    </FormField>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )
      ))}

      {rows.length < max ? (
        <div>
          <AppButton type="button" variant="outline" size="sm" onClick={add}>
            <Plus size={16} /> {addLabel}
          </AppButton>
        </div>
      ) : (
        <p className="tw-m-0 tw-text-xs tw-text-content-subtle">That is the most this page shows ({max}).</p>
      )}
    </div>
  );
};

export default ListEditor;
